import { useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";

import { ROUTE_PATHS, ROUTE_PREFIXES } from "@/shared/constants/route-paths";

import type { WorkspaceDisplayParams } from "./types";
import type { ItemFormatFilter } from "../types";

const VALID_FORMATS: ItemFormatFilter[] = [
  "all",
  "website",
  "image",
  "text",
  "pdf",
  "video",
];
const DUPLICATE_FORMATS: ItemFormatFilter[] = ["website", "image", "text"];

export function useWorkspaceContext(): WorkspaceDisplayParams {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  return useMemo(() => {
    const pathname = location.pathname;
    const rawFormat = searchParams.get("format");
    const format = rawFormat && VALID_FORMATS.includes(rawFormat as ItemFormatFilter)
      ? (rawFormat as ItemFormatFilter)
      : undefined;
    const keyword = searchParams.get("keyword") || undefined;
    const searchKeyword = searchParams.get("q") || keyword;

    if (pathname === ROUTE_PATHS.MINE) {
      return {
        type: "items" as const,
        folder_id: "0",
        format,
        keyword,
      };
    }

    if (pathname === ROUTE_PATHS.MINE_ALL) {
      return {
        type: "items" as const,
        folder_id: "-1",
        format,
        keyword,
      };
    }

    if (pathname === ROUTE_PATHS.MINE_DUPLICATES) {
      return {
        type: "items" as const,
        folder_id: "-1",
        duplicatesOnly: true,
        format: format && DUPLICATE_FORMATS.includes(format) ? format : undefined,
      };
    }

    if (pathname === ROUTE_PATHS.MINE_SEARCH) {
      return {
        type: "items" as const,
        folder_id: "-1",
        format,
        keyword: searchKeyword,
      };
    }

    if (pathname.startsWith(ROUTE_PREFIXES.MINE_FOLDERS)) {
      return {
        type: "folder" as const,
        folder_id: params.folderId,
        format,
        keyword,
      };
    }

    if (pathname.startsWith(ROUTE_PREFIXES.MINE_TAGS)) {
      return {
        type: "tag" as const,
        tagId: params.tagId,
        format,
        keyword,
      };
    }

    return {
      type: "items" as const,
      folder_id: "-1",
      format,
      keyword,
    };
  }, [location.pathname, params.folderId, params.tagId, searchParams]);
}
